import React, { useCallback, useEffect, useState, useRef } from "react";
import { ObservableQuery } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";

import rethrowIfNotAborted from "apollo/rethrowIfNotAborted";
import FlashContext from "components/FlashMessages/FlashContext";
import WebhookDeliveriesList from "components/WebhookDeliveries";
import useTypedContext from "hooks/useTypedContext";

import { GET_AUDIT_TRAIL_DELIVERIES } from "./gql";
import { AuditTrailWebhookGql } from "./types";

const AuditTrailWebhookDeliveries = () => {
  const { onError } = useTypedContext(FlashContext);
  const [active, setActive] = useState(false);
  const deliveriesRef = useRef<HTMLDivElement>(null);

  const [getDeliveries, { loading, data, fetchMore }] = useLazyQuery<AuditTrailWebhookGql>(
    GET_AUDIT_TRAIL_DELIVERIES,
    {
      nextFetchPolicy: "cache-first",
    }
  );

  const token = data?.auditTrailWebhook?.deliveries?.nextToken;
  const hasData = Boolean(data?.auditTrailWebhook);

  useEffect(() => {
    if (!hasData) return;

    const elTop = deliveriesRef.current?.offsetTop;

    if (elTop) {
      window.scrollTo({ top: elTop - 75, behavior: "smooth" });
    }
  }, [hasData]);

  const handleClick = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement>) => {
      evt.preventDefault();

      if (!active && !token) {
        getDeliveries({ variables: { nextToken: null } }).catch(rethrowIfNotAborted);
      }

      setActive(!active);
    },
    [active, token, getDeliveries]
  );

  const updateQuery: ObservableQuery.FetchMoreOptions<
    AuditTrailWebhookGql,
    { nextToken?: string | null }
  >["updateQuery"] = (prev, { fetchMoreResult }) => {
    if (!fetchMoreResult) return prev;

    if (
      fetchMoreResult &&
      prev?.auditTrailWebhook?.deliveries?.nodes &&
      fetchMoreResult?.auditTrailWebhook?.deliveries?.nodes
    ) {
      return {
        auditTrailWebhook: {
          ...fetchMoreResult.auditTrailWebhook,
          deliveries: {
            ...fetchMoreResult.auditTrailWebhook.deliveries,
            nextToken: fetchMoreResult.auditTrailWebhook.deliveries.nextToken,
            nodes: [
              ...(prev?.auditTrailWebhook?.deliveries.nodes || []),
              ...(fetchMoreResult?.auditTrailWebhook?.deliveries.nodes || []),
            ],
          },
        },
      };
    }

    return { auditTrailWebhook: { ...prev.auditTrailWebhook } };
  };

  const loadMore = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement>) => {
      const asyncFn = async (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        try {
          if (fetchMore) {
            await fetchMore({ variables: { nextToken: token }, updateQuery });
          }
        } catch (error) {
          onError(error);
        }
      };

      void asyncFn(evt);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, updateQuery]
  );

  return (
    <WebhookDeliveriesList
      innerRef={deliveriesRef}
      items={data?.auditTrailWebhook?.deliveries?.nodes}
      collapsed={active}
      loading={loading}
      handleClick={handleClick}
      token={token}
      loadMore={loadMore}
    />
  );
};

export default AuditTrailWebhookDeliveries;
